import { decode } from "base64-arraybuffer";
import { observer } from "mobx-react-lite";
import { useContext } from "react";

import { DefaultButton, useTheme } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { DocumentEntityType } from "@libs/gateways/inbox/InboxGateway.dtos.ts";
import { BillingScreenWrapper } from "@modules/billing/screens/shared-components/BillingScreenWrapper.tsx";
import { StatementGenerateModal } from "@modules/billing/screens/statement/components/StatementGenerateModal.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { DocumentViewerDialog } from "@ui-components/document-viewer/DocumentViewerDialog.tsx";
import { getPDFStyles } from "@ui-components/document-viewer/pdf.styles.ts";
import { When } from "@ui-components/withPerm.tsx";

import { StatementItemsContext } from "./context/StatementItemsContext.ts";
import { StatementItemsHelper } from "./context/StatementItemsHelper.ts";
import { StatementsList } from "./StatementItemList.tsx";

const StatementItemsScreen = () => {
  const stores = useStores();
  const helper = new StatementItemsHelper(stores);
  return (
    <StatementItemsContext.Provider value={helper}>
      <StatementItemsScreenBase />
    </StatementItemsContext.Provider>
  );
};

const StatementItemsScreenBase = observer(() => {
  const { billing } = useStores();
  const theme = useTheme();
  const {
    ui: { statementPrintDocumentData, setStatementPrintDocumentData }
  } = billing;

  const { setIsStatementNewModalVisible, isStatementNewModalVisible } =
    useContext(StatementItemsContext);

  const { noMarginsLandscapePdfstyles } = getPDFStyles(theme);

  const closeDocumentViewer = () => {
    setStatementPrintDocumentData(undefined);
  };

  return (
    <BillingScreenWrapper
      headerButtons={
        <When permission={Permission.StatementWrite}>
          <DefaultButton onClick={() => setIsStatementNewModalVisible(true)}>
            New statement run
          </DefaultButton>
        </When>
      }
    >
      {isStatementNewModalVisible && <StatementGenerateModal />}
      {statementPrintDocumentData && (
        <DocumentViewerDialog
          hideDownloadButton={true}
          pageStyle={noMarginsLandscapePdfstyles}
          getDocument={() => {
            if (statementPrintDocumentData) {
              return {
                documentId: "",
                extension: statementPrintDocumentData.contentType,
                entityId: "",
                entityType: DocumentEntityType.Patient,
                name: "Print statement",
                previewUri: new Uint8Array(
                  decode(statementPrintDocumentData.content)
                )
              };
            }
            throw Error("no Statement document to print");
          }}
          closeDocumentViewer={closeDocumentViewer}
        />
      )}
      <StatementsList />
    </BillingScreenWrapper>
  );
});

// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default StatementItemsScreen;
